import { useEffect, useMemo, useState } from "react";
import { capitalize } from "lodash";
import classNames from "classnames";
import dayjs from "dayjs";

import AscTable from "../AscTable";
import { CheckboxInp } from "../AscAdminStudentsList/AscCustomCheckBox/CheckboxInp";
import { AdminCalendarIcon } from "../../AscIcons";
import styles from "./AscAdminInvoiceHistory.module.scss";
import AscParagraph from "../../AscTypography/AscParagraph/AscParagraph";

export interface AscAdminInvoiceHistoryProps {
  invoiceData: any;
  handleCheckedCells?: (checkedIds: string[]) => void;
  isCreatedDate?: boolean;
}

export enum InvoiceHistoryEnum {
  genCheck = "genCheck",
  invoiceNumber = "invoiceNumber",
  date = "date",
  createdAt = "createdAt",
  amount = "amount",
  status = "status",
  particulars = "particulars",
  genCheckMain = "genCheckMain",
}

export type FilterFnOfTableT = (
  rows: {
    allCells: any[];
    cells: any[];
    depth: number;
    id: string;
    index: number;
    original: { [key: string]: any };
    values: { [key: string]: any };
  }[],
  columnIds: string[],
  filterValue: any
) => any[];

export interface IMyColumn {
  Header: any;
  accessor: string;
  Cell?: any;
  enableSorting?: boolean;
  filter?: FilterFnOfTableT;
}

export default function AscAdminInvoiceHistory({
  invoiceData,
  handleCheckedCells,
  isCreatedDate = false,
}: AscAdminInvoiceHistoryProps) {
  const tableData: {
    [InvoiceHistoryEnum.genCheck]: boolean;
    [InvoiceHistoryEnum.invoiceNumber]: number;
    [InvoiceHistoryEnum.date]: string;
    [InvoiceHistoryEnum.createdAt]: string;
    [InvoiceHistoryEnum.status]: any;
    [InvoiceHistoryEnum.amount]: number;
    [InvoiceHistoryEnum.particulars]: string;
  }[] = useMemo(() => [...invoiceData], [invoiceData]);

  const [mainGenCheckbox, setMainGenCheckbox] = useState<boolean>(() => {
    const boo = tableData.some((x) => x.genCheck !== true);
    const allRowsSelected = boo === false;

    return allRowsSelected;
  });

  const [genCheckForEachRow, setGenCheckForEachRow] = useState<boolean[]>(
    () => {
      const arr = tableData.map((x) => x.genCheck);
      return arr;
    }
  );

  useEffect(() => {
    const boo = genCheckForEachRow.some((x) => x !== true);
    const allRowsSelected = boo === false;
    setMainGenCheckbox(allRowsSelected);
  }, [genCheckForEachRow]);
  // Update mainGenCheckbox whenever tableData changes
  useEffect(() => {
    const boo = tableData.some((x) => x.genCheck !== true);
    const allRowsSelected = boo === false;
    setMainGenCheckbox(allRowsSelected);
  }, [tableData]);

  // Update genCheckForEachRow whenever tableData changes
  useEffect(() => {
    const arr = tableData.map((x) => x.genCheck);
    setGenCheckForEachRow(arr);
  }, [tableData]);

  useEffect(() => {
    const checkedIds = invoiceData.reduce(
      (acc: string[], item: { id: string }, index: number) => {
        if (genCheckForEachRow[index]) {
          acc.push(item.id);
        }
        return acc;
      },
      [] as string[]
    );
    handleCheckedCells?.(checkedIds);
  }, [genCheckForEachRow, tableData]);
  const tableColumns = useMemo(() => {
    const columns = [
      {
        Header: (
          <CheckboxInp
            id={InvoiceHistoryEnum.genCheckMain}
            name={InvoiceHistoryEnum.genCheckMain}
            onBlur={() => 0}
            isChecked={mainGenCheckbox}
            textualValue={InvoiceHistoryEnum.genCheckMain}
            tryToChange={(change: any) => {
              setGenCheckForEachRow((prev) =>
                prev.map((_x) => change.isChecked || false)
              );
            }}
            className={styles.genCheckMain}
          />
        ),
        accessor: InvoiceHistoryEnum.genCheck, // accessor is the "key" in the data

        Cell: (cell: { row: { index: any } }) => {
          const ind = cell.row.index;
          const nm = `${InvoiceHistoryEnum.genCheck}${ind}`;
          return (
            <CheckboxInp
              id={nm}
              name={nm}
              onBlur={() => 0}
              isChecked={genCheckForEachRow[ind]}
              textualValue={String(ind)}
              tryToChange={(change: any) => {
                setGenCheckForEachRow((prev) =>
                  prev.map((x, i) => {
                    if (ind === i) {
                      return change.isChecked || false;
                    }
                    return x;
                  })
                );
              }}
              className={styles.coolCheck}
            />
          );
        },
      },

      {
        Header: () => {
          return (
            <AscParagraph className={styles.header}>
              Invoice number
            </AscParagraph>
          );
        },
        accessor: InvoiceHistoryEnum.invoiceNumber,
        enableSorting: true,
      },

      {
        Header: () => {
          return <AscParagraph className={styles.header}>Date</AscParagraph>;
        },
        accessor: isCreatedDate
          ? InvoiceHistoryEnum.createdAt
          : InvoiceHistoryEnum.date,
        enableSorting: true,
        Cell: (cell: { value: any }) => {
          return (
            <div className={styles.dateContainer}>
              <AdminCalendarIcon className={styles.icon} />
              <AscParagraph className={styles.date}>
                {dayjs(cell.value).format("DD MMMM YYYY")}
              </AscParagraph>
            </div>
          );
        },
      },

      {
        Header: () => {
          return <AscParagraph className={styles.header}>Credits</AscParagraph>;
        },
        accessor: InvoiceHistoryEnum.amount, // accessor is the "key" in the data
        enableSorting: true,
      },

      {
        Header: () => {
          return (
            <AscParagraph className={styles.header}>Particulars</AscParagraph>
          );
        },
        accessor: InvoiceHistoryEnum.particulars, // accessor is the "key" in the data
      },

      {
        Header: () => {
          return <AscParagraph className={styles.header}>Status</AscParagraph>;
        },
        accessor: InvoiceHistoryEnum.status,
        enableSorting: true,
        Cell: (cell: { value: any }) => {
          return (
            <span
              className={classNames(
                styles.status,
                styles[cell.value.toLowerCase()]
              )}
            >
              {capitalize(cell?.value)}
            </span>
          );
        },
      },
    ];

    return columns;
  }, [genCheckForEachRow, mainGenCheckbox, tableData]);

  return (
    <AscTable
      tHead={styles.tHead}
      myTable={styles.myTable}
      tHeadRow={styles.tHeadRow}
      tH={styles.tH}
      tBody={styles.tBody}
      tRow={styles.tRow}
      pureCellFlow={styles.pureCellFlow}
      tD={styles.tD}
      tBodyValue={styles.tBodyValue}
      className={styles.table}
      tableColumns={tableColumns}
      tableData={invoiceData}
      showHeader={true}
      eachPageSize={50}
    />
  );
}
